import { AudioPlayer } from "@/components/hymns/AudioPlayer";
import { DevDeleteMediaButton } from "@/components/hymns/DevDeleteMediaButton";
import { FavoritesWidget } from "@/components/hymns/FavoritesWidget";
import { HymnPageLayout } from "@/components/hymns/HymnPageLayout";
import { HymnSearchBar } from "@/components/hymns/HymnSearchBar";
import { SongDetailFlatListPanel } from "@/components/hymns/SongDetailFlatListPanel";
import { WorshipExporters } from "@/components/hymns/WorshipExporters";
import {
  getHymnBySectionAndNumber,
  getRandomHymn,
  Hymn,
} from "@/services/hymnsApi";
import {
  copyToClipboard,
  extractVerseLines,
  getHymnSlug,
  getLanguageLabel,
  parseHymnVerses,
  scrollToTop,
} from "@/utils";
import { useFavorites } from "@/utils/export-favorites";
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Heart,
  RefreshCw,
  Share2,
  Shuffle,
  Sparkles,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

const PageSongDetail = () => {
  const { sectionCode, number } = useParams<{
    sectionCode: string;
    number: string;
  }>();
  const navigate = useNavigate();

  const num = parseInt(number ? number.split("-")[0] : "1", 10);
  const [hymn, setHymn] = useState<Hymn | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const STORAGE_KEY_FONT_SIZE = "egarizim_hymn_font_size";

  const [fontSizeClass, setFontSizeClass] = useState<
    "normal" | "large" | "xlarge"
  >(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        const saved = localStorage.getItem(STORAGE_KEY_FONT_SIZE);
        if (saved === "normal" || saved === "large" || saved === "xlarge") {
          return saved;
        }
      } catch (e) {
        console.warn("Failed to read font size from localStorage", e);
      }
    }
    return "normal";
  });

  useEffect(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        localStorage.setItem(STORAGE_KEY_FONT_SIZE, fontSizeClass);
      } catch (e) {
        console.warn("Failed to save font size to localStorage", e);
      }
    }
  }, [fontSizeClass]);

  const { isCurrentFavorited, toggle: toggleCurrentFavorite } = useFavorites(
    sectionCode,
    num,
  );

  const handleToggleFavorite = () => {
    if (!hymn) return;
    if (!hymn.media_url && !isCurrentFavorited) {
      toast.error("Audio non disponible pour ce cantique.");
      return;
    }
    const { isFavorited } = toggleCurrentFavorite(hymn);
    if (isFavorited) {
      toast.success(`Chant N° ${hymn.number} ajouté aux favoris !`);
    } else {
      toast.info(`Chant N° ${hymn.number} retiré des favoris`);
    }
  };

  // Synchronize browser address bar with canonical slug if needed
  useEffect(() => {
    if (hymn && hymn.title && sectionCode) {
      const canonicalSlug = getHymnSlug(hymn.number, hymn.title);
      const targetPath = `/chants-desperance/${encodeURIComponent(sectionCode)}/${canonicalSlug}`;
      if (window.location.pathname !== targetPath) {
        window.history.replaceState(null, "", targetPath);
      }
    }
  }, [hymn, sectionCode]);

  const loadSong = useCallback(async () => {
    if (!sectionCode || isNaN(num)) return;
    setLoading(true);
    setHasError(false);
    try {
      const hymnData = await getHymnBySectionAndNumber(sectionCode, num);
      setHymn(hymnData);
    } catch (err) {
      console.error("Error loading hymn detail:", err);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  }, [sectionCode, num]);

  useEffect(() => {
    loadSong();
  }, [loadSong]);

  const rawSection = hymn?.section || sectionCode || "";
  const cleanSection = rawSection.replace(/\s*\([^)]*\)\s*$/, "").trim();

  const badgeInfo = {
    badge: sectionCode ? sectionCode.split("-")[0] : "",
    name: cleanSection,
    languageName: getLanguageLabel(hymn?.language),
    languageCode: hymn?.language || "",
  };

  const canonicalUrl =
    typeof window !== "undefined" ? window.location.href : "";

  const handleShare = async () => {
    if (!hymn) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `N° ${hymn.number} - ${hymn.title}`,
          text: `Chant ${hymn.section} N° ${hymn.number} : ${hymn.title}`,
          url: canonicalUrl || window.location.href,
        });
      } catch (err) {
        // User cancelled share
      }
    } else {
      await copyToClipboard(canonicalUrl || window.location.href);
      toast.success("Lien du chant copié dans le presse-papier !");
    }
  };

  const [loadingRandom, setLoadingRandom] = useState<boolean>(false);

  const handleRandomHymn = async () => {
    if (loadingRandom) return;
    setLoadingRandom(true);
    try {
      const randomHymn = await getRandomHymn(sectionCode);
      if (randomHymn) {
        scrollToTop();
        const slug = getHymnSlug(randomHymn.number, randomHymn.title);
        navigate(
          `/chants-desperance/${encodeURIComponent(randomHymn.section_code || sectionCode || "")}/${slug}`,
        );
      } else {
        toast.error("Aucun chant aléatoire trouvé.");
      }
    } catch (err) {
      console.error("Error fetching random hymn:", err);
      toast.error("Impossible de récupérer un chant aléatoire.");
    } finally {
      setLoadingRandom(false);
    }
  };

  const goToHymn = (targetNumber: number) => {
    if (targetNumber < 1) return;
    scrollToTop();
    navigate(
      `/chants-desperance/${encodeURIComponent(sectionCode || "")}/${targetNumber}`,
    );
  };

  // Formattage des strophes et refrains
  const renderVerses = () => {
    if (!hymn) return null;

    const parsedVerses = parseHymnVerses(hymn.verses, hymn.full_text);

    if (parsedVerses.length > 0) {
      return (
        <div className="space-y-4 sm:space-y-8">
          {parsedVerses.map((verse: any, idx: number) => {
            const rawType = (verse.type || "").toLowerCase();
            const lines = extractVerseLines(verse);
            const cleanContent = lines.join("\n");

            const isChorus =
              rawType === "chorus" ||
              rawType === "refrain" ||
              rawType === "kè" ||
              rawType === "choeur" ||
              (typeof verse.content === "string" &&
                (verse.content.startsWith("Kè") ||
                  verse.content.startsWith("Kè:") ||
                  verse.content.startsWith("Refrain") ||
                  verse.content.startsWith("Refrain:")));

            if (isChorus) {
              return (
                <div
                  key={idx}
                  className="relative p-3.5 sm:p-6 rounded-xl sm:rounded-2xl bg-amber-500/10 border-l-4 border-amber-400 backdrop-blur-md shadow-inner text-amber-100"
                >
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2 text-[11px] sm:text-xs font-black uppercase tracking-wider text-amber-300">
                    <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    <span>Kè / Refrain</span>
                  </div>
                  <div className="whitespace-pre-line font-medium leading-relaxed italic">
                    {cleanContent}
                  </div>
                </div>
              );
            }

            return (
              <div key={idx} className="relative pl-0 sm:pl-2">
                <div className="flex items-start gap-2.5 sm:gap-4">
                  <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-slate-800 border border-slate-700 text-sky-400 font-bold text-xs sm:text-sm flex items-center justify-center flex-shrink-0 mt-0.5 shadow">
                    {verse.number || idx + 1}
                  </span>
                  <div className="whitespace-pre-line leading-relaxed text-slate-100 font-normal">
                    {cleanContent}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <p className="text-slate-400 italic">Paroles en cours de numérisation.</p>
    );
  };

  const renderClickableSource = (source: string) => {
    if (!source) return null;
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
    const parts = source.split(urlRegex);

    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        {parts.map((part, index) => {
          if (/^(https?:\/\/|www\.)/i.test(part)) {
            const href = /^https?:\/\//i.test(part) ? part : `https://${part}`;
            return (
              <a
                key={index}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-400 hover:text-sky-300 underline underline-offset-4 inline-flex items-center gap-1 transition-colors font-medium break-all"
              >
                <span>{part}</span>
                <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
              </a>
            );
          }
          return <span key={index}>{part}</span>;
        })}
      </span>
    );
  };

  const pageTitle = hymn
    ? `N° ${hymn.number} ${hymn.title} - ${badgeInfo.name} | Église Garizim`
    : `Chant N° ${num} - ${badgeInfo.name} | Église Garizim`;
  const pageDescription = hymn
    ? `Paroles complètes et audio du cantique N° ${hymn.number} « ${hymn.title} » du recueil ${badgeInfo.name}. Chantez et louez avec l'Église Garizim.`
    : `Paroles complètes et audio du cantique N° ${num} du recueil ${badgeInfo.name}.`;

  return (
    <HymnPageLayout>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="music.song" />
        <meta property="og:site_name" content="Église Garizim" />
        <meta
          property="og:image"
          content="https://ik.imagekit.io/4apzi8lz0/egarizim/chant-esperance-api.jpg"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta
          name="twitter:image"
          content="https://ik.imagekit.io/4apzi8lz0/egarizim/chant-esperance-api.jpg"
        />
      </Helmet>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-6 mt-5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 items-start">
          {/* Left Sidebar: Liste complète des cantiques du recueil (Desktop / Non-mobile uniquement) */}
          <SongDetailFlatListPanel
            sectionCode={sectionCode}
            currentNumber={num}
            badgeInfo={badgeInfo}
            isPrimaryLoading={loading}
            className="hidden lg:flex lg:col-span-4 xl:col-span-3 lg:sticky lg:top-20 lg:self-start h-[580px] max-h-[580px] z-20"
          />

          {/* Right Main Content: Lecteur & Paroles */}
          <div className="lg:col-span-8 xl:col-span-9 min-w-0">
            {/* Navigation Breadcrumb */}
            <div className="flex flex-wrap items-center justify-between gap-2.5 sm:gap-3 mb-4 sm:mb-6">
              <Link
                to={`/chants-desperance/${encodeURIComponent(sectionCode || "")}`}
                onClick={() => scrollToTop()}
                className="inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-lg sm:rounded-xl bg-amber-400 hover:bg-amber-300 text-black text-xs sm:text-sm font-bold shadow-md shadow-amber-400/20 transition-all group cursor-pointer"
              >
                <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:-translate-x-1 transition-transform" />
                <span>Retour à {badgeInfo.name}</span>
              </Link>

              {/* Navigation Rapide Précédent / Suivant */}
              <div className="flex items-center gap-1.5 sm:gap-2">
                <button
                  onClick={() => goToHymn(num - 1)}
                  disabled={num <= 1}
                  className="px-2.5 py-1.5 sm:px-3.5 sm:py-2 rounded-lg sm:rounded-xl bg-amber-400 hover:bg-amber-300 disabled:opacity-30 disabled:cursor-not-allowed text-black text-xs sm:text-sm font-bold flex items-center gap-1 sm:gap-1.5 shadow-md shadow-amber-400/20 transition-all cursor-pointer"
                  title="Cantique précédent"
                >
                  <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Précédent</span>
                </button>
                <button
                  onClick={() => goToHymn(num + 1)}
                  className="px-2.5 py-1.5 sm:px-3.5 sm:py-2 rounded-lg sm:rounded-xl bg-amber-400 hover:bg-amber-300 text-black text-xs sm:text-sm font-bold flex items-center gap-1 sm:gap-1.5 shadow-md shadow-amber-400/20 transition-all cursor-pointer"
                  title="Cantique suivant"
                >
                  <span className="hidden sm:inline">Suivant</span>
                  <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
              </div>
            </div>

            {/* Skeleton Loader animé */}
            {loading && (
              <div className="rounded-2xl sm:rounded-3xl bg-slate-900/90 border border-slate-700 p-6 sm:p-12 animate-pulse space-y-4 sm:space-y-6">
                <div className="flex gap-2">
                  <div className="h-5 w-14 bg-slate-800 rounded-full"></div>
                  <div className="h-5 w-18 bg-slate-800 rounded-full"></div>
                </div>
                <div className="h-8 bg-slate-800 rounded-lg w-3/4"></div>
                <div className="h-4 bg-slate-800 rounded-lg w-1/3"></div>
                <div className="h-16 bg-slate-800/80 rounded-xl"></div>
                <div className="space-y-3 pt-4">
                  <div className="h-4 bg-slate-800/60 rounded w-full"></div>
                  <div className="h-4 bg-slate-800/60 rounded w-5/6"></div>
                  <div className="h-4 bg-slate-800/60 rounded w-4/6"></div>
                </div>
              </div>
            )}

            {/* Lecteur de cantique avec Super Contraste */}
            {!loading && hymn && (
              <div className="rounded-2xl sm:rounded-3xl bg-slate-900/95 border border-slate-700/80 shadow-xl sm:shadow-2xl backdrop-blur-xl overflow-hidden">
                {/* Zone d'en-tête */}
                <div className="p-4 sm:p-8 md:p-10 border-b border-slate-800/80 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950">
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                    {hymn.category && (
                      <span className="px-2.5 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700 text-[11px] sm:text-xs font-semibold">
                        {hymn.category}
                      </span>
                    )}
                  </div>

                  <h1 className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight leading-tight">
                    N° {hymn.number} - {hymn.title}
                  </h1>
                  <p className="text-slate-400 text-xs sm:text-sm mt-1 sm:mt-2">
                    {hymn.section}
                  </p>

                  {/* Barre d'outils utilitaires : Recherche rapide (Gauche), Favoris & Partager (Droite) */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-4 sm:mt-6 pt-3.5 sm:pt-5 border-t border-slate-800/80">
                    <div className="w-full sm:max-w-xs md:max-w-sm">
                      <HymnSearchBar
                        size="compact"
                        placeholder="Rechercher par titre"
                      />
                    </div>

                    <div className="flex items-center justify-end gap-1.5 sm:gap-2 flex-shrink-0">
                      <button
                        onClick={handleToggleFavorite}
                        disabled={!hymn.media_url && !isCurrentFavorited}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-xl text-xs font-semibold border transition-all ${
                          isCurrentFavorited
                            ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 shadow-sm shadow-emerald-500/10 cursor-pointer"
                            : !hymn.media_url
                              ? "bg-slate-900/60 text-slate-500 border-slate-800 opacity-50 cursor-not-allowed"
                              : "bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700 cursor-pointer"
                        }`}
                        title={
                          isCurrentFavorited
                            ? "Retirer des favoris"
                            : !hymn.media_url
                              ? "Audio non disponible pour ce cantique"
                              : "Ajouter aux favoris"
                        }
                      >
                        <Heart
                          className={`w-3.5 h-3.5 transition-transform ${
                            isCurrentFavorited
                              ? "fill-emerald-400 text-emerald-400 scale-110"
                              : ""
                          }`}
                        />
                        <span className="hidden sm:inline">
                          {isCurrentFavorited
                            ? "Retirer du Favori -"
                            : "Ajouter au Favori +"}
                        </span>
                        <span className="inline sm:hidden">
                          {isCurrentFavorited ? "Favori -" : "Favori +"}
                        </span>
                      </button>

                      <button
                        onClick={handleShare}
                        className="p-1.5 sm:p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 transition-colors cursor-pointer"
                        title="Partager ce cantique"
                      >
                        <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Lecteur Audio Intégré & Favoris Compact */}
                <div className="p-4 sm:p-8 bg-slate-950/60 border-b border-slate-800">
                  <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 sm:gap-6 items-stretch">
                    <div className="xl:col-span-7 flex flex-col justify-center">
                      <AudioPlayer
                        mediaUrl={hymn.media_url}
                        title={`N° ${hymn.number} - ${hymn.title}`}
                        subtitle={`${hymn.section}`}
                        onPrevious={
                          num > 1 ? () => goToHymn(num - 1) : undefined
                        }
                        onNext={() => goToHymn(num + 1)}
                        hasPrevious={num > 1}
                        hasNext={true}
                      />
                    </div>
                    <div className="xl:col-span-5 flex flex-col">
                      <FavoritesWidget currentHymn={hymn} compact={true} />
                    </div>
                  </div>
                </div>

                {/* Outil Destructif Développeur (Localhost uniquement) */}
                {!loading && hymn && hymn.media_url && (
                  <DevDeleteMediaButton
                    hymn={hymn}
                    sectionCode={sectionCode}
                    num={num}
                    onDeleted={loadSong}
                  />
                )}

                {/* Paroles du cantique avec Super Contraste */}
                <div
                  className={`p-4 sm:p-6 md:p-8 ${
                    fontSizeClass === "normal"
                      ? "text-sm sm:text-lg"
                      : fontSizeClass === "large"
                        ? "text-base sm:text-xl"
                        : "text-lg sm:text-2xl"
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2.5 sm:gap-3 mb-4 sm:mb-6 pb-3 sm:pb-4 border-b border-slate-800/80">
                    <WorshipExporters hymn={hymn} />

                    {/* Sélecteur de taille de texte */}
                    <div className="flex items-center gap-1 bg-slate-900/100 p-1 rounded-lg sm:rounded-xl border border-slate-800 text-xs text-slate-300">
                      <span className="px-1.5 sm:px-2 text-slate-400 font-semibold text-[11px] sm:text-xs">
                        Taille:
                      </span>
                      <button
                        onClick={() => setFontSizeClass("normal")}
                        className={`px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md sm:rounded-lg font-bold text-xs transition-all ${
                          fontSizeClass === "normal"
                            ? "bg-sky-500 text-slate-950"
                            : "hover:text-white"
                        }`}
                      >
                        A
                      </button>
                      <button
                        onClick={() => setFontSizeClass("large")}
                        className={`px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md sm:rounded-lg font-bold text-xs sm:text-sm transition-all ${
                          fontSizeClass === "large"
                            ? "bg-sky-500 text-slate-950"
                            : "hover:text-white"
                        }`}
                      >
                        A+
                      </button>
                      <button
                        onClick={() => setFontSizeClass("xlarge")}
                        className={`px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md sm:rounded-lg font-bold text-xs sm:text-base transition-all ${
                          fontSizeClass === "xlarge"
                            ? "bg-sky-500 text-slate-950"
                            : "hover:text-white"
                        }`}
                      >
                        A++
                      </button>
                    </div>
                  </div>
                  {renderVerses()}

                  {hymn.source_data && (
                    <div className="mt-8 pt-4 border-t border-slate-800/80 flex flex-wrap items-center gap-2 text-xs sm:text-sm text-slate-400">
                      <span className="font-semibold text-slate-300">
                        Source :
                      </span>
                      {renderClickableSource(hymn.source_data)}
                    </div>
                  )}
                </div>

                {/* Navigation inférieure */}
                <div className="p-4 sm:p-8 bg-slate-900/100 border-t border-slate-800 flex items-center justify-between gap-2 sm:gap-3">
                  <button
                    onClick={() => goToHymn(num - 1)}
                    disabled={num <= 1}
                    className="inline-flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-bold text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span>Prec. N° {num - 1}</span>
                  </button>

                  <button
                    onClick={handleRandomHymn}
                    disabled={loadingRandom}
                    className="inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 hover:text-amber-200 text-xs sm:text-sm font-bold transition-all shadow-sm active:scale-95 disabled:opacity-50"
                    title="Chant au hasard"
                  >
                    <Shuffle
                      className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${loadingRandom ? "animate-spin" : ""}`}
                    />
                    <span className="hidden xs:inline sm:inline">
                      Chant aléatoire
                    </span>
                  </button>

                  <button
                    onClick={() => goToHymn(num + 1)}
                    className="inline-flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-bold text-amber-400 hover:text-amber-300 transition-colors"
                  >
                    <span>Suiv. N° {num + 1}</span>
                    <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Erreur de chargement API */}
            {!loading && hasError && (
              <div className="text-center py-20 px-6 bg-slate-900/90 rounded-3xl border border-rose-500/30 p-8 max-w-xl mx-auto shadow-2xl">
                <div className="w-14 h-14 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center mx-auto mb-4 text-rose-400">
                  <AlertTriangle className="w-7 h-7" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Erreur de connexion au serveur
                </h2>
                <p className="text-slate-300 text-sm mb-6 leading-relaxed">
                  Impossible de récupérer le cantique N° {number} ({sectionCode}
                  ) depuis la base de données. Veuillez vérifier votre connexion
                  Internet.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <button
                    onClick={() => loadSong()}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sky-500 text-slate-950 font-bold hover:bg-sky-400 transition-all shadow-lg hover:scale-105"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Réessayer</span>
                  </button>
                  <Link
                    to="/chants-desperance"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-800 text-slate-200 font-semibold hover:bg-slate-700 transition-colors border border-slate-700"
                  >
                    Retour aux recueils
                  </Link>
                </div>
              </div>
            )}

            {/* Cantique non trouvé (404) */}
            {!loading && !hasError && !hymn && (
              <div className="text-center py-20 bg-slate-900/90 rounded-3xl border border-slate-700 p-8">
                <BookOpen className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                <h2 className="text-2xl font-bold text-white mb-2">
                  Cantique non trouvé
                </h2>
                <p className="text-slate-400 text-sm mb-6">
                  Le chant N° {number} dans le recueil {sectionCode} n'a pas pu
                  être trouvé dans ce recueil.
                </p>
                <Link
                  to="/chants-desperance"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sky-500 text-slate-950 font-bold hover:bg-sky-400 transition-colors shadow-lg"
                >
                  Retour aux recueils
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </HymnPageLayout>
  );
};

export default PageSongDetail;
